import React from 'react'
import { useDropzone } from 'react-dropzone'
import { IconCloudUpload, IconFileText, IconX } from '@tabler/icons-react'
import classNames from 'classnames'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'

import './CsvDropzone.scss'

// We don't reuse DragAndDropFileUploader (Formik-coupled, image-focused,
// renders FilePreview which reads state.audioIngestion) because the CSV flow
// has a different presentation (parsed pill + immediate text read) and no
// surrounding form. Both components share react-dropzone directly; the
// duplication is the ~6-line useDropzone config, which isn't worth abstracting.

// Soft cap so a multi-GB drop doesn't OOM the browser tab when FileReader
// reads the whole file as text. 50MB is generous for CSV (~500k rows at
// 100 chars each) and matches what the server-side row-count cap permits.
const MAX_CSV_MB = 50
const helperTextFor = (maxSizeMb, extra) =>
  `Supported file types: csv. Max file size: ${maxSizeMb}MB${extra ? `, ${extra}` : ''}`

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (loadEvent) => {
      resolve(loadEvent.target.result)
    }
    reader.onerror = () => {
      reject(reader.error)
    }
    reader.readAsText(file, 'UTF-8')
  })

/**
 * @import import { CsvDropzone } from '@/components/forms/CSVUploadForms/CsvDropzone'
 * @param {File} [props.file] The chosen file; when set, the picker is replaced by a summary row
 * @param {Function} props.onFileSelected Called with `{ file, csvText }` once the file is read
 * @param {Function} props.onFileCleared Called when the chosen file is removed
 * @param {number} [props.maxSizeMb] Largest accepted file in MB (default 50); also sets the default hint
 * @param {string} [props.extraHint] Appended to the default hint, e.g. an endpoint's row cap
 * @param {string} [props.helperText] Replaces the generated hint entirely
 */
export const CsvDropzone = ({
  file,
  onFileSelected,
  onFileCleared,
  maxSizeMb = MAX_CSV_MB,
  extraHint,
  helperText = null,
}) => {
  const resolvedHelperText = helperText || helperTextFor(maxSizeMb, extraHint)
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    maxSize: maxSizeMb * 1024 * 1024,
    onDropAccepted: async (acceptedFiles) => {
      const [picked] = acceptedFiles
      if (!picked) {
        return
      }
      try {
        const csvText = await readFileAsText(picked)
        onFileSelected({ file: picked, csvText })
      } catch (readError) {
        toast.error('Could not read the CSV file', { cause: readError })
      }
    },
    onDropRejected: (rejections) => {
      const errorMessage = rejections[0]?.errors[0]?.message
      toast.error(errorMessage || 'File not accepted')
    },
  })

  if (file) {
    return (
      <div className="csv-dropzone csv-dropzone--has-file" data-testid="csv-dropzone-loaded">
        <div className="csv-dropzone__label">Selected file</div>
        <div className="csv-dropzone__file-row">
          <IconFileText className="icon-svg" />
          <div className="csv-dropzone__file-meta">
            <div className="csv-dropzone__file-name">{file.name}</div>
          </div>
          <Pill success small>
            parsed
          </Pill>
          <Button
            square
            small
            basic
            onClick={onFileCleared}
            data-testid="csv-dropzone-clear"
            aria-label="Remove file"
          >
            <IconX />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      {...getRootProps({
        className: classNames('csv-dropzone', {
          'csv-dropzone--drag-active': isDragActive,
        }),
      })}
      data-testid="csv-dropzone"
    >
      <input {...getInputProps()} type="file" data-testid="csv-dropzone-input" />
      <IconCloudUpload className="csv-dropzone__icon" />
      <h3 className="csv-dropzone__title">Drag your CSV file here or click to upload</h3>
      <p className="csv-dropzone__hint muted-text">{resolvedHelperText}</p>
      <Button
        primary
        onClick={(event) => {
          event.stopPropagation()
          open()
        }}
      >
        Upload CSV
      </Button>
    </div>
  )
}
